import { useMutation, useQuery } from "@apollo/client/react";
import cx from "classnames";
import { useEffect, useId, useState } from "react";

import CodeEditor from "components/CodeEditor";
import FlashContext from "components/FlashMessages/FlashContext";
import { ChevronDown, ChevronRight, ExpandLeft } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ButtonIcon from "ds/components/ButtonIcon";
import Typography from "ds/components/Typography";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import { TrackAnalyticsEventProperties } from "shared/Analytics";
import { EMPTY_POLICY_SAMPLE_RESULTS_MSG } from "shared/Policy/constants";
import { SIMULATE_POLICY_V2 } from "shared/Policy/gql";
import { Policy, PolicyEngineType, PolicyType } from "types/generated";
import { beautifyCode } from "utils/strings";

import PolicyDiffCallout from "../PolicyDiffCallout";
import { EMPTY_INPUT_MSG } from "./constants";
import { GET_POLICY_EVALUATION_RECORDS, GET_POLICY_EVALUATION_SAMPLE } from "./gql";
import PolicyDetailsInputSelect from "./InputSelect";
import styles from "./styles.module.css";

type PolicySimulationProps = {
  body: string;
  policyId: string;
  type: PolicyType;
  engineType: PolicyEngineType;
  isSimulationVisible: boolean;
  setIsSimulationVisible: (value: boolean) => void;
  defaultAnalyticsProperties?: TrackAnalyticsEventProperties;
  className?: string;
  evaluationKey?: string;
};

const PolicySimulation = ({
  body,
  type,
  policyId,
  engineType,
  isSimulationVisible,
  setIsSimulationVisible,
  defaultAnalyticsProperties,
  className,
  evaluationKey,
}: PolicySimulationProps) => {
  const { onError, reportError } = useTypedContext(FlashContext);
  const [isInputEditorExpanded, setIsInputEditorExpanded] = useState(false);
  const [isOutputEditorExpanded, setIsOutputEditorExpanded] = useState(false);
  const [sampleInput, setSampleInput] = useState(EMPTY_INPUT_MSG);
  const [simulationOutput, setSimulationOutput] = useState(EMPTY_POLICY_SAMPLE_RESULTS_MSG);
  const [evaluationBody, setEvaluationBody] = useState("");
  const wrapperId = useId();

  const handleInputChange = (value?: string) => {
    setSampleInput(value || "");
  };

  const trackSegmentAnalyticsEvent = useAnalytics({
    defaultCallbackTrackProperties: defaultAnalyticsProperties,
  });

  const [currentPolicyInputKey, setCurrentPolicyInputKey] = useState(evaluationKey || "");

  const { data } = useQuery<{ policy: Policy }>(GET_POLICY_EVALUATION_RECORDS, {
    variables: {
      policyId,
    },
  });

  useEffect(() => {
    if (
      data?.policy?.evaluationRecords &&
      data?.policy?.evaluationRecords?.length > 0 &&
      !evaluationKey
    ) {
      setCurrentPolicyInputKey(data.policy.evaluationRecords[0].key);
    }
  }, [data?.policy?.evaluationRecords, evaluationKey]);

  const { loading: evaluationSampleInputLoading, data: evaluationSampleData } = useQuery<{
    policy: Policy;
  }>(GET_POLICY_EVALUATION_SAMPLE, {
    variables: {
      policyId,
      key: currentPolicyInputKey,
    },
    skip: !currentPolicyInputKey,
  });

  useEffect(() => {
    if (evaluationSampleData?.policy?.evaluationSample) {
      setSampleInput(beautifyCode(evaluationSampleData.policy.evaluationSample.input));
      setEvaluationBody(evaluationSampleData.policy.evaluationSample.body);
    } else if (evaluationSampleData && !evaluationSampleData.policy.evaluationSample) {
      reportError({ message: "We couldn't find input body for the simulation" });
    }
  }, [evaluationSampleData, reportError]);

  const [simulatePolicyV2, { loading: simulationLoading }] = useMutation(SIMULATE_POLICY_V2, {
    variables: {
      input: {
        body,
        input: sampleInput,
        type,
        engineType,
        libraries: null,
      },
    },
  });

  const toggleSimulationVisibility = () => {
    trackSegmentAnalyticsEvent("Policy Simulation Panel Clicked", {
      status: isSimulationVisible ? "close" : "open",
    });
    setIsSimulationVisible(!isSimulationVisible);
  };

  const handleSimulation = async () => {
    trackSegmentAnalyticsEvent("Policy Simulate Clicked");
    try {
      const simulationResult = await simulatePolicyV2().then(
        // eslint-disable-next-line @cspell/spellchecker
        ({ data }) => data?.policySimulatev2 ?? ""
      );
      if (simulationResult) {
        setIsInputEditorExpanded(false);
        setSimulationOutput(beautifyCode(simulationResult));
      }
    } catch (error) {
      onError(error);
      setSimulationOutput(EMPTY_POLICY_SAMPLE_RESULTS_MSG);
    }
  };

  const handleInputKeyChange = (value: string) => {
    setIsOutputEditorExpanded(false);
    setSimulationOutput(EMPTY_POLICY_SAMPLE_RESULTS_MSG);
    setCurrentPolicyInputKey(value);
  };

  const toggleInputEditor = () => {
    if (evaluationSampleInputLoading) return;

    if (isOutputEditorExpanded && !isInputEditorExpanded) {
      setIsOutputEditorExpanded(false);
    }

    setIsInputEditorExpanded(!isInputEditorExpanded);
  };

  const toggleOutputEditor = () => {
    if (evaluationSampleInputLoading) return;

    if (isInputEditorExpanded && !isOutputEditorExpanded) {
      setIsInputEditorExpanded(false);
    }

    setIsOutputEditorExpanded(!isOutputEditorExpanded);
  };

  const isDiffWarningVisible = evaluationBody && evaluationBody !== body;

  return (
    <Box
      className={cx(styles.simulationWrapper, isSimulationVisible && styles.expanded, className)}
      direction="column"
      id={wrapperId}
    >
      <Box
        relative
        className={cx(styles.header, { [styles.collapsed]: !isSimulationVisible })}
        align="center"
        justify="between"
        gap="medium"
        padding="large"
      >
        <Typography
          className={cx(styles.title, {
            [styles.hidden]: !isSimulationVisible,
          })}
          variant="p-t6"
          tag="h3"
        >
          Simulation panel
        </Typography>
        <Typography
          className={cx(styles.simulationPanelText, { [styles.hidden]: isSimulationVisible })}
          variant="p-t7"
          tag="span"
          aria-hidden
        >
          Simulation panel
        </Typography>
        <ButtonIcon
          className={cx(styles.visibilityToggle, {
            [styles.expanded]: isSimulationVisible,
          })}
          onPress={toggleSimulationVisibility}
          icon={ExpandLeft}
          iconRotate={isSimulationVisible ? "180" : undefined}
          variant="ghost"
          aria-controls={wrapperId}
          aria-expanded={isSimulationVisible}
        >
          {isSimulationVisible ? "Collapse simulation panel" : "Expand simulation panel"}
        </ButtonIcon>
      </Box>
      <Box
        className={cx(styles.simulationBody, isSimulationVisible && styles.visible)}
        direction="column"
      >
        {isDiffWarningVisible && (
          <PolicyDiffCallout
            disabledActions={!isSimulationVisible}
            body={body}
            evaluationBody={evaluationBody}
          />
        )}

        <Box
          className={styles.simulationInputWrapper}
          padding="large"
          __deprecatedGap="1rem"
          align="end"
        >
          <PolicyDetailsInputSelect
            inputs={data?.policy?.evaluationRecords || []}
            handleInputKeyChange={handleInputKeyChange}
            currentPolicyInputKey={currentPolicyInputKey}
          />
          <Button variant="contrast" onPress={handleSimulation} loading={simulationLoading}>
            Simulate
          </Button>
        </Box>
        <Box
          // FYI: it allows to change size of editors when diff warning is visible
          key={String(isDiffWarningVisible)}
          direction="column"
          className={styles.editors}
        >
          <Box
            direction="column"
            className={cx(styles.editorSection, {
              [styles.expanded]: isInputEditorExpanded,
              [styles.hidden]: isOutputEditorExpanded,
            })}
          >
            <Box className={styles.editorHeader} align="center" gap="medium">
              <ButtonIcon
                disabled={evaluationSampleInputLoading}
                onPress={toggleInputEditor}
                icon={isInputEditorExpanded ? ChevronDown : ChevronRight}
                variant="ghost"
              >
                {isInputEditorExpanded ? "Collapse" : "Expand"}
              </ButtonIcon>
              <Typography color="secondary" tag="span" variant="p-t6">
                Input
              </Typography>
            </Box>
            <div className={cx(styles.editorWrapper, isOutputEditorExpanded && styles.hidden)}>
              {evaluationSampleInputLoading && <PageLoading />}
              {!evaluationSampleInputLoading && (
                <CodeEditor
                  className={styles.editor}
                  body={sampleInput}
                  onChange={handleInputChange}
                  language="rego"
                />
              )}
            </div>
          </Box>
          <Box
            direction="column"
            className={cx(styles.editorSection, {
              [styles.expanded]: isOutputEditorExpanded,
              [styles.hidden]: isInputEditorExpanded,
            })}
          >
            <Box className={styles.editorHeader} align="center" gap="medium">
              <ButtonIcon
                disabled={evaluationSampleInputLoading}
                onPress={toggleOutputEditor}
                icon={isOutputEditorExpanded ? ChevronDown : ChevronRight}
                variant="ghost"
              >
                {isOutputEditorExpanded ? "Collapse" : "Expand"}
              </ButtonIcon>
              <Typography color="secondary" tag="span" variant="p-t6">
                Output
              </Typography>
            </Box>
            <div className={cx(styles.editorWrapper, isInputEditorExpanded && styles.hidden)}>
              <CodeEditor
                className={styles.editor}
                readOnly
                body={simulationOutput}
                language="json"
              />
            </div>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PolicySimulation;
